import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { arrayMove, horizontalListSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Tab as MUITab, Tabs as MUITabs } from "@mui/material";
import React, { HTMLAttributes, useEffect, useRef, useState } from "react";

import { cn } from "../../utils/styling";
import { Icon } from "../Icon";

const TabScrollButton = (
  props: Omit<HTMLAttributes<HTMLDivElement>, "direction"> & { direction: "left" | "right" }
) => {
  const { direction, ...other } = props;

  return (
    <div className="cursor-pointer" {...other}>
      <Icon
        className={`w-4 cursor-pointer ${direction === "left" && "rotate-180"}`}
        color="var(--color-foreground-primary)"
        iconName="ChevronRight"
      />
    </div>
  );
};

type TabPropsBase = {
  hasDeleteIcon?: boolean;
  onDelete?: () => void;
  setValue?: (newValue?: string) => void;
  value?: string;
};
export default function DraggableTabs({
  activeTabIndex,
  onAddTab,
  setActiveTabIndex,
  setTabs,
  tabs,
}: {
  activeTabIndex: number;
  onAddTab: () => void;
  setActiveTabIndex: (index: number) => void;
  setTabs: (tabs: { id: string; label: string }[]) => void;
  tabs: { id: string; label: string; props: TabPropsBase }[];
}) {
  const newTabRef = useRef<HTMLDivElement>(null);
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = tabs.findIndex((t) => t.id === active.id);
      const newIndex = tabs.findIndex((t) => t.id === over.id);
      const newTabs = arrayMove(tabs, oldIndex, newIndex);

      setTabs(newTabs);
      if (activeTabIndex === oldIndex) setActiveTabIndex(newIndex);
      else if (activeTabIndex >= Math.min(oldIndex, newIndex) && activeTabIndex <= Math.max(oldIndex, newIndex)) {
        if (oldIndex < newIndex) setActiveTabIndex(activeTabIndex - 1);
        else setActiveTabIndex(activeTabIndex + 1);
      }
    }
  };

  return (
    <div className="flex items-center">
      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <SortableContext items={tabs.map((t) => t.id)} strategy={horizontalListSortingStrategy}>
          <MUITabs
            centered={false}
            classes={{
              scrollableX: "!my-0 !border-8  border-ds-soft pl-3 pr-2 !border-y-0",
            }}
            ScrollButtonComponent={(props) => <TabScrollButton {...props} />}
            scrollButtons={true}
            value={activeTabIndex}
            variant="scrollable"
          >
            {tabs.map((tab, index) => (
              <SortableTab
                id={tab.id}
                isActive={index === activeTabIndex}
                key={tab.id}
                onClick={() => setActiveTabIndex(index)}
                props={tab.props}
              />
            ))}
            <MUITab className="dashboard-tab-base font-ds-headline-lg " label="+" onClick={onAddTab} ref={newTabRef} />
          </MUITabs>
        </SortableContext>
      </DndContext>
    </div>
  );
}

const DashboardTab = ({
  hasDeleteIcon,
  hovered,
  isActive,
  onDelete,
  setValue,
  textColor,
  value,
}: TabPropsBase & {
  hovered?: boolean;
  isActive?: boolean;
  textColor?: string;
}) => {
  const onChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setValue?.(e.target.value);
  };

  const inputRef = useRef<HTMLInputElement>(null);
  const spanHiddenRef = useRef<HTMLSpanElement>(null);
  const spanVisibleRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (spanHiddenRef.current && inputRef.current) {
      inputRef.current.style.width = `${spanHiddenRef.current.offsetWidth + 24}px`;
      if (spanVisibleRef.current) spanVisibleRef.current.style.width = `${spanHiddenRef.current.offsetWidth + 24}px`;
    }
  }, [value, isActive]);

  return (
    <>
      {isActive && (
        <>
          <input
            className="h-8 min-w-[5rem]   bg-transparent  p-2 text-left capitalize font-ds-body-semibold-md"
            onChange={onChange}
            ref={inputRef}
            style={{
              border: "none",
            }}
            value={value}
          />
          <span
            className="absolute capitalize  font-ds-body-semibold-md"
            ref={spanHiddenRef}
            style={{ height: 0, visibility: "hidden", whiteSpace: "pre" }}
          >
            {value || " "}
          </span>
        </>
      )}
      {!isActive && (
        <span
          className="h-8 min-w-[5rem] cursor-pointer bg-transparent  p-2 text-center capitalize font-ds-body-semibold-md"
          ref={spanVisibleRef}
        >
          {value}
        </span>
      )}
      {hasDeleteIcon && hovered && (
        <span
          className="absolute right-2 top-2 rounded p-[3px] pb-[0px] outline-1 hover:outline " //TODO: Remove this pb workaround
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.();
          }}
          style={{
            outlineColor: textColor,
          }}
        >
          <Icon className="h-[1rem] w-[1rem]" color={textColor} iconName="Close" />
        </span>
      )}
    </>
  );
};

export const SortableTab = ({
  id,
  isActive,
  onClick,
  props,
}: {
  id: string;
  isActive: boolean;
  onClick: () => void;
  props: TabPropsBase;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const textColor = isActive ? "var(--color-element-primary-on-primary)" : "var(--color-foreground-secondary)";

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [hovered, setHovered] = useState(false);

  return (
    <div
      className={cn(
        "dashboard-tab",
        isActive && "!border-none bg-lds-element-primary text-lds-element-primary-on",
        !isActive && "hover:bg-lds-selection-subtle"
      )}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      ref={setNodeRef}
      style={style}
    >
      <div {...attributes} {...listeners}>
        <span className="cursor-move ">
          <Icon className="ml-1 !w-2" color="currentColor" iconName="DragHandle" size="lg" />
        </span>
      </div>
      <DashboardTab hovered={hovered} isActive={isActive} textColor={textColor} {...props} />
    </div>
  );
};
